import { useEffect, useRef, useState } from "react";

import type { OnboardingRunStatus, OnboardingStepStatus } from "./types";

interface StepLogLine {
	stream: string;
	text: string;
	at: string;
}

export interface OnboardingStreamState {
	runStatus: OnboardingRunStatus | null;
	stepStatuses: Record<number, OnboardingStepStatus>;
	stepErrors: Record<number, string | null>;
	logsByStep: Record<number, StepLogLine[]>;
	connected: boolean;
	error: string | null;
}

interface RawEventData {
	status?: OnboardingRunStatus | OnboardingStepStatus;
	ordinal?: number;
	stream?: string;
	chunk?: string;
	at?: string;
	error_message?: string | null;
}

const INITIAL: OnboardingStreamState = {
	runStatus: null,
	stepStatuses: {},
	stepErrors: {},
	logsByStep: {},
	connected: false,
	error: null,
};

/**
 * Subscribe to the SSE event stream at
 * `GET /onboarding/{runId}/events`. Returns the collected state —
 * re-renders as new events arrive. Closes the EventSource on unmount
 * (or when ``runId`` changes).
 */
export function useOnboardingStream(runId: string | undefined): OnboardingStreamState {
	const [state, setState] = useState<OnboardingStreamState>(INITIAL);
	const esRef = useRef<EventSource | null>(null);

	useEffect(() => {
		if (!runId) {
			setState(INITIAL);
			return;
		}
		// EventSource sends session cookies automatically on same-origin.
		const es = new EventSource(`/onboarding/${runId}/events`);
		esRef.current = es;

		es.onopen = () => {
			setState((s) => ({ ...s, connected: true, error: null }));
		};
		es.onerror = () => {
			// Browsers automatically reconnect on transient errors; we
			// just reflect the disconnected state in the UI.
			setState((s) => ({ ...s, connected: false }));
		};

		const onRunStatus = (e: MessageEvent<string>) => {
			try {
				const data = JSON.parse(e.data) as RawEventData;
				if (data.status) {
					setState((s) => ({ ...s, runStatus: data.status as OnboardingRunStatus }));
				}
			} catch {
				/* malformed event — ignore */
			}
		};
		const onStepStatus = (e: MessageEvent<string>) => {
			try {
				const data = JSON.parse(e.data) as RawEventData;
				if (data.ordinal != null && data.status) {
					const ord = data.ordinal;
					setState((s) => ({
						...s,
						stepStatuses: {
							...s.stepStatuses,
							[ord]: data.status as OnboardingStepStatus,
						},
						stepErrors: {
							...s.stepErrors,
							[ord]: data.error_message ?? null,
						},
					}));
				}
			} catch {
				/* ignore */
			}
		};
		const onStepLog = (e: MessageEvent<string>) => {
			try {
				const data = JSON.parse(e.data) as RawEventData;
				if (data.ordinal != null && data.chunk != null) {
					const ord = data.ordinal;
					setState((s) => {
						const existing = s.logsByStep[ord] ?? [];
						// Cap per-step log buffer in memory — the detail
						// API can page earlier chunks when needed.
						const updated = [
							...existing,
							{
								stream: data.stream ?? "stdout",
								text: data.chunk ?? "",
								at: data.at ?? new Date().toISOString(),
							},
						].slice(-500);
						return {
							...s,
							logsByStep: { ...s.logsByStep, [ord]: updated },
						};
					});
				}
			} catch {
				/* ignore */
			}
		};

		es.addEventListener("run.status", onRunStatus as EventListener);
		es.addEventListener("step.status", onStepStatus as EventListener);
		es.addEventListener("step.log", onStepLog as EventListener);

		return () => {
			es.removeEventListener("run.status", onRunStatus as EventListener);
			es.removeEventListener("step.status", onStepStatus as EventListener);
			es.removeEventListener("step.log", onStepLog as EventListener);
			es.close();
			esRef.current = null;
		};
	}, [runId]);

	return state;
}
